import styled from "@emotion/styled"
import { Paper } from "matsuri-ui"
import { theme } from "@/theme"

export const Main = styled.main`
    background-color: ${theme.palette.intentions.cleanerBackground.main};
`

export const PropertyBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background-color: ${theme.palette.intentions.primary.light};
    color: ${theme.palette.intentions.primary.dark};
    font-size: 15px;
    font-weight: bold;
`

export const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    /* 下は固定フッターのクリアランス（ResizeObserver の contentRect が
       フッターの padding を含まず paddingBottom が不足するため上乗せする） */
    padding: 16px 16px 64px;
`

export const CenteredBody = styled.div`
    display: grid;
    place-items: center;
    gap: 16px;
    min-height: 50vh;
    padding: 24px;
    text-align: center;
`

export const QuestionTitle = styled.div`
    font-size: 20px;
    font-weight: bold;
    line-height: 1.5;
    color: ${theme.palette.intentions.text.primary};
`

export const QuestionLead = styled.div`
    font-size: 14px;
    line-height: 1.6;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

export const Card = styled(Paper)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 12px;
`

export const CardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: bold;
    color: ${theme.palette.intentions.text.primary};
`

export const TitleAccent = styled.span`
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background-color: ${theme.palette.intentions.primary.main};
`

// Q1 / 2 のような進捗表示。
export const StepLabel = styled.div`
    font-size: 13px;
    font-weight: bold;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

// 分類の具体例。判断材料なので、行ごとに区切り線を入れて1項目ずつ読ませる。
export const ExampleList = styled.ul`
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    border-radius: 8px;
    background-color: ${theme.palette.intentions.cleanerBackground.main};
    list-style: none;
`

export const ExampleRow = styled.li`
    padding: 12px 14px;
    border-bottom: 1px solid ${theme.palette.intentions.cleanerGray.light};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.palette.intentions.text.primary};

    &:last-of-type {
        border-bottom: none;
    }
`

export const InstructionBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border: 1px solid #ffe0a3;
    border-radius: 8px;
    background-color: #fff8e1;
    color: #8a6d3b;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.5;
`

// バナー内で見出しと補足を積むときに使う。
export const BannerLines = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

export const WarningBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 8px;
    background-color: ${theme.palette.intentions.secondary.light};
    color: ${theme.palette.intentions.secondary.dark};
    font-size: 14px;
    font-weight: bold;
    line-height: 1.5;
`

export const CategoryBadge = styled.span<{ valuable: boolean }>`
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    border-radius: 999px;
    background-color: ${({ valuable }) =>
        valuable
            ? theme.palette.intentions.secondary.light
            : theme.palette.intentions.primary.light};
    color: ${({ valuable }) =>
        valuable
            ? theme.palette.intentions.secondary.dark
            : theme.palette.intentions.primary.dark};
    font-size: 12px;
    font-weight: bold;
`

// 報告内容の項目。ラベルと値を横に並べると文として繋がって読めてしまうため、
// ラベルを上・値を下の縦積みにする（ゴミストッカーの鍵情報と同じ形）。
export const DetailList = styled.dl`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
`

export const DetailRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

export const DetailLabel = styled.dt`
    font-size: 12px;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

export const DetailValue = styled.dd`
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: ${theme.palette.intentions.text.primary};
    overflow-wrap: anywhere;
`

// 報告IDは WORK への問い合わせで読み上げ・コピーするため、省略せず全桁を出す。
export const ReportIdValue = styled(DetailValue)`
    font-family: monospace;
    user-select: text;
`

export const ReportedAt = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

export const PhotoSectionLabel = styled.div`
    font-size: 13px;
    font-weight: bold;
    color: ${theme.palette.intentions.text.primary};
`

export const PhotoCount = styled.span`
    margin-left: 12px;
    font-weight: normal;
    color: ${theme.palette.intentions.cleanerGray.dark};
`

// 1fr の3列にすると、枚数やカードの内側幅で同じ写真が別サイズになってしまう。
// 固定幅にして、どの画面でも同じ大きさ・左詰めで並ぶようにする。
export const ThumbnailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, 96px);
    gap: 8px;
`

export const ThumbnailWrapper = styled.div`
    position: relative;
    aspect-ratio: 1 / 1;
`

export const Thumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`

// 一覧の写真。報告は最大3枚なので、1枚目だけ出さず全部並べて
// どれでも拡大できるようにする（2枚目以降を確認する手段が他に無いため）。
export const SummaryThumbnailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, 64px);
    gap: 8px;
`

export const SummaryThumbnailWrapper = styled.div`
    position: relative;
    width: 64px;
    height: 64px;
    cursor: pointer;
`

export const SummaryThumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`

// 報告日時と取り消しを同じ行に置き、カードの主役（報告内容）より弱く見せる。
export const CardActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 4px;
    border-top: 1px solid ${theme.palette.intentions.cleanerGray.light};
`

export const RemoveButton = styled.button`
    position: absolute;
    top: -6px;
    right: -6px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background-color: rgba(0, 0, 0, 0.6);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`

export const StatusOverlay = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 12px;
    text-align: center;
    white-space: pre-line;
`

export const AddTile = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border: 1.5px dashed ${theme.palette.intentions.cleanerGray.main};
    border-radius: 8px;
    background-color: transparent;
    color: ${theme.palette.intentions.cleanerGray.dark};
    font-size: 13px;
    text-align: left;
    cursor: pointer;

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`

export const AddTileIcon = styled.span`
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    display: grid;
    place-items: center;
    border: 1.5px dashed ${theme.palette.intentions.cleanerGray.main};
    border-radius: 8px;
    color: ${theme.palette.intentions.primary.main};
`

export const DoneBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 32px 16px 16px;
    text-align: center;
`

export const DoneIcon = styled.div`
    display: grid;
    place-items: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: ${theme.palette.intentions.primary.light};
    color: ${theme.palette.intentions.primary.main};
`
